import React, { useMemo, useCallback, useState } from 'react';
import { Box, Grid, GridProps } from '@mui/material';
import styled from 'styled-components';
import { ProgramSummarySelectMenu } from 'spw-esa-projects/program-summary/select-menu';
import {
  DownloadButtonBox,
  HeadcountBox,
  HeadcountLabelBox,
} from 'spw-esa-projects/program-summary/styles';
import { HeadcountLevelTooltip } from 'spw-esa-projects/program-summary/headcount-level-tooltip';
import { HeadcountOptions } from 'spw-esa-projects/program-summary/headcount-options';
import { ProgramSummaryParams } from 'spw-redux/modules/program-summary/types';
import ClientNamesToggle from 'spw-esa-projects/client-names-toggle';
import {
  Switch,
  SwitchFormControlLabel,
  SwitchLabel,
} from 'spw-components/global/styles';
import DownloadButtonWithOptions, {
  DownloadOption,
} from 'spw-components/collab/download-button-with-options';
import { DownloadOptionsHeader } from 'spw-projects/components/program-summary-styles';
import {
  ESAProjectReportType,
  ESAReportAttributes,
  PNG,
  ProgramSummaryExportableType,
} from 'spw-redux/modules/reports/types';
import { prepareAndExecuteESAReportDownload } from 'spw-redux/modules/downloads/actions';
import { REPORT_TYPES } from 'spw-esa-projects/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentFloor as selectCurrentFloor,
  currentProject,
  selectCurrentBuilding,
  selectCurrentUserIsViewOnly,
} from 'spw-redux/modules/projects/selectors';
import { selectClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/selectors';
import { selectIsDownloadingIndividualReport } from 'spw-redux/modules/downloads/selectors';
import { selectBreakdown } from 'spw-redux/modules/program-summary/esa/selectors';
import { ProgramSummaryExportableTypes } from 'spw-redux/modules/reports/constants';
import { colors } from 'spw-theme';
import { isNil } from 'ramda';
import SpaceTypeColorsPopover from 'spw-projects/space_types/space-type-colors-popover';
import useUnitConversion from 'spw-components/unit-conversion/hooks';

const Container = styled((props: GridProps) => <Grid container {...props} />)`
  position: sticky;
  top: 52px;
  z-index: 1;
  background: white;
  padding-top: 8px;
`;

const SelectBox = styled(Box)`
  && {
    margin-left: 10px;
  }
`;

const EditColorsButton = styled.div`
  cursor: pointer;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  color: ${colors.mainBlue};

  &.disabled {
    color: ${colors.disabledIcons};
    cursor: default;
  }

  img {
    margin: 0 8px 0 16px;
  }
`;

type ProgramSummaryDownloadOption = DownloadOption & {
  exportableType: ProgramSummaryExportableType;
};

enum DownloadOptions {
  OnePager = 'One Page Report',
  AllCardsIndividually = 'All Cards Individually',
  SummaryData = 'Summary Data',
}

type DownloadRequestOptions = {
  downloadOption?: ProgramSummaryDownloadOption;
  downloadName?: string;
};

const DOWNLOAD_OPTIONS: ProgramSummaryDownloadOption[] = [
  {
    label: DownloadOptions.OnePager,
    format: PNG,
    exportableType: ProgramSummaryExportableTypes.ONE_PAGER,
  },
  {
    label: DownloadOptions.AllCardsIndividually,
    format: PNG,
    exportableType: ProgramSummaryExportableTypes.ALL_CARDS_INDIVIDUALLY,
  },
  {
    label: DownloadOptions.SummaryData,
    format: PNG,
    exportableType: ProgramSummaryExportableTypes.SUMMARY_DATA,
  },
];

type Props = {
  initialSelection: ProgramSummaryParams;
  showRsf: boolean;
  toggleShowRsf: React.Dispatch<React.SetStateAction<boolean>>;
};

const Header: React.FC<Props> = ({
  initialSelection,
  showRsf,
  toggleShowRsf,
}: Props) => {
  const dispatch = useDispatch();
  const { getUnit } = useUnitConversion();
  const currentFloor = useSelector(selectCurrentFloor);
  const breakdown = useSelector(selectBreakdown);
  const project = useSelector(currentProject);
  const building = useSelector(selectCurrentBuilding);

  const [initialDownloadName, setInitialDownloadName] = useState('');
  const [editColorsAnchorEl, setEditColorsAnchorEl] = useState(null);

  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const clientFacingNamesAreVisible = useSelector(
    selectClientFacingNamesAreVisible
  );

  const isDownloadingIndividualReport = useSelector(
    selectIsDownloadingIndividualReport
  );

  const isEditColorsButtonDisabled = useMemo(() => {
    return !isNil(editColorsAnchorEl) || currentUserIsViewOnly;
  }, [editColorsAnchorEl, currentUserIsViewOnly]);

  const downloadButtonIsDisabled = useMemo(() => {
    for (const key in breakdown) {
      if (Object.prototype.hasOwnProperty.call(breakdown, key)) {
        if (breakdown[key].length) return false;
      }
    }

    return true;
  }, [breakdown]);

  const exportProgramSummary = ({
    downloadName,
    downloadOption,
  }: DownloadRequestOptions) => {
    dispatch(
      prepareAndExecuteESAReportDownload({
        reportAttributes: {
          reportType: REPORT_TYPES.PROGRAM_SUMMARY as ESAProjectReportType,
          format: downloadOption.format,
          exportableType: downloadOption.exportableType,
          entityId: currentFloor ? currentFloor.id : building.id,
          entityType: currentFloor ? 'FLOOR' : 'BUILDING',
          showRsf,
          shouldShowClientFacingNames: clientFacingNamesAreVisible,
        } as ESAReportAttributes,
        outputFileName: `${downloadName}.zip`,
      })
    );
  };

  const downloadOptions = useMemo(() => {
    const options = DOWNLOAD_OPTIONS.concat(
      (project?.spaceTypeCategories || [])
        .filter((spaceTypeCategory) => {
          return breakdown[spaceTypeCategory.defaultSpaceTypeCategory.category]
            ?.length;
        })
        .map((spaceTypeCategory) => {
          return {
            label: spaceTypeCategory.name,
            format: PNG,
            exportableType: `${spaceTypeCategory.defaultSpaceTypeCategory.category}Cards` as ProgramSummaryExportableType,
          };
        })
    );

    return options.map((option) => {
      return {
        ...option,
        onClick: () => {
          const optionName =
            option.label === DownloadOptions.AllCardsIndividually
              ? 'All Cards'
              : option.label;

          setInitialDownloadName(`${project.projectName}_${optionName}`);
        },
      };
    });
  }, [project?.spaceTypeCategories, breakdown, project?.projectName]);

  const handleEditColorsButton = useCallback((e: React.BaseSyntheticEvent) => {
    setEditColorsAnchorEl(e.currentTarget);
  }, []);

  const handleOnCloseColorsPopover = useCallback(() => {
    setEditColorsAnchorEl(null);
  }, []);

  return (
    <Container>
      <Grid item xs={12}>
        <Box display="flex" mb={2}>
          <Box flex={1} display="flex" alignItems="center">
            <SelectBox display="flex" alignItems="center" mr={3}>
              <ProgramSummarySelectMenu initialSelection={initialSelection} />
            </SelectBox>
            <Box display="flex" flexDirection="column">
              <HeadcountLabelBox display="flex" alignItems="center">
                <Box>HEADCOUNT SOURCE:</Box>
                <HeadcountLevelTooltip />
              </HeadcountLabelBox>
            </Box>
            <HeadcountBox display="flex" alignItems="center" ml={2}>
              <HeadcountOptions />
            </HeadcountBox>
          </Box>
          <ClientNamesToggle />
          <SwitchFormControlLabel
            data-cy="rsf-toggle-switch"
            control={
              <Switch
                checked={showRsf}
                onChange={() => toggleShowRsf((prev) => !prev)}
                color="default"
              />
            }
            label={
              <SwitchLabel style={{ fontWeight: 'normal', paddingTop: 1 }}>
                {getUnit('rentable-area')}
              </SwitchLabel>
            }
            labelPlacement="start"
          />

          <EditColorsButton
            onClick={
              !isEditColorsButtonDisabled ? handleEditColorsButton : null
            }
            className={isEditColorsButtonDisabled ? 'disabled' : ''}
          >
            <img
              src={`/images/program-summary/${
                isEditColorsButtonDisabled ? 'settings-gray' : 'settings'
              }.svg`}
              alt="settings"
            />
            <span>EDIT COLORS</span>
          </EditColorsButton>
          {!isNil(editColorsAnchorEl) && (
            <SpaceTypeColorsPopover
              anchorEl={editColorsAnchorEl}
              onClose={handleOnCloseColorsPopover}
            />
          )}
          <DownloadButtonBox
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            pt={0.25}
          >
            <DownloadButtonWithOptions
              initialDownloadName={initialDownloadName}
              maxDownloadNameLength={45}
              errorMessage="A name is required"
              isBusy={isDownloadingIndividualReport}
              disabled={downloadButtonIsDisabled}
              options={downloadOptions}
              onSubmit={exportProgramSummary}
              popoverContainerStyle={{
                width: '100%',
                paddingBottom: '8px',
              }}
              exportButtonClasses="pt-0"
              header={
                <DownloadOptionsHeader>DOWNLOAD OPTIONS</DownloadOptionsHeader>
              }
            />
          </DownloadButtonBox>
        </Box>
      </Grid>
    </Container>
  );
};

export default Header;
